import { CollectionInsights } from '@/components/features/CollectionInsights/CollectionInsights';
import { PageHeader } from '@/components/shared/PageHeader';
import { SocialMediaLinks } from '@/components/shared/SocialMediaLinks';
import { getCollectionsInfinite } from '@/hooks/use-collections-infinite';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { getSSRChain } from '@/lib/reservoir/get-ssr-chain';
import { routes } from '@/utils/routes';
import { Container } from '@chakra-ui/react';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { type GetServerSideProps, type NextPage } from 'next';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';

const InsightsPage: NextPage<Props> = () => {
  const { t } = useTranslation('insights');
  const marketplaceChain = useMarketplaceChain();

  return (
    <>
      <NextSeo title={t('page-title')} description={t('page-description')} />
      <PageHeader
        containerVariant="breakout"
        title={t('page-title')}
        pages={[
          {
            name: t('common:crumbs.home'),
            href: routes.home(marketplaceChain.routePrefix),
          },
          {
            name: t('page-title'),
            href: routes.insights(marketplaceChain.routePrefix),
          },
        ]}
      />

      <Container mb="space.80" variant="breakout">
        <CollectionInsights />
      </Container>

      <SocialMediaLinks mt={{ base: 'space.88', md: rem(200) }} />
    </>
  );
};

type Params = {
  chain: string;
};

type Props = {
  dehydratedState: unknown;
};

export const getServerSideProps: GetServerSideProps<Props, Params> = async ctx => {
  const SSRClient = new QueryClient();
  const chain = getSSRChain(ctx);

  await SSRClient.prefetchInfiniteQuery(
    getCollectionsInfinite(chain, {
      params: { sortBy: 'allTimeVolume' },
    }),
  );

  return {
    props: {
      dehydratedState: dehydrate(SSRClient),
    },
  };
};

export default InsightsPage;
